/**
 * Record Detail Panel - shows the full selected record with key summary fields
 * and a JSON tree view of all fields.
 */

import React, { useState } from 'react';
import { useTheme, fontSize, fontWeight, spacing, Button, Icon } from '@fti/design-system';
import type { RecordDetailPanelProps } from './contracts';
import { isHttpUrl, copyToClipboard } from './format';
import { JsonTree } from './JsonTree';

export function RecordDetailPanel({
  row,
  position,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: RecordDetailPanelProps) {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(false);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  // Handle copy with feedback
  const handleCopy = async () => {
    const success = await copyToClipboard(row);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Safe access to potentially missing or wrong-typed fields
  const title = typeof row.title === 'string' ? row.title : undefined;
  const url = typeof row.url === 'string' && isHttpUrl(row.url) ? row.url : undefined;
  const source = row.source && typeof row.source === 'object' ? row.source as Record<string, unknown> : undefined;
  const sourceName = source && typeof source.name === 'string' ? source.name : undefined;
  const publishedDate = typeof row.publishedDate === 'string' ? row.publishedDate : undefined;
  const content = typeof row.content === 'string' ? row.content : undefined;

  const headingStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: mutedColor,
    textTransform: 'uppercase',
    fontWeight: fontWeight.semibold,
    marginBottom: spacing.xs,
    marginTop: spacing.md,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sticky top bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: theme.colors.background.paper,
          borderBottom: `1px solid ${borderColor}`,
          padding: spacing.md,
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.sm,
          }}
        >
          <div style={{ fontSize: fontSize.sm, color: textColor, fontWeight: fontWeight.medium }}>
            Record {position} of {total}
          </div>
          <div style={{ display: 'flex', gap: spacing.xs }}>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon name="chevron-left" size="sm" />}
              onClick={onPrev}
              disabled={!hasPrev}
              title="Previous record ([)"
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              rightIcon={<Icon name="chevron-right" size="sm" />}
              onClick={onNext}
              disabled={!hasNext}
              title="Next record (])"
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon name="copy" size="sm" />}
              onClick={handleCopy}
            >
              {copied ? 'Copied' : 'Copy JSON'}
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', padding: spacing.md }}>
        {/* Prominent summary */}
        {(title || sourceName || publishedDate || url) && (
          <div style={{ marginBottom: spacing.md }}>
            {title && (
              <div
                style={{
                  fontSize: fontSize.sm,
                  fontWeight: fontWeight.semibold,
                  color: textColor,
                  marginBottom: spacing.xs,
                }}
              >
                {title}
              </div>
            )}
            {(sourceName || publishedDate) && (
              <div style={{ fontSize: fontSize.xs, color: mutedColor }}>
                {sourceName}
                {sourceName && publishedDate && ' · '}
                {publishedDate}
              </div>
            )}
            {url && (
              <div style={{ marginTop: spacing.xs }}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: fontSize.xs,
                    color: theme.colors.primary[500],
                    textDecoration: 'none',
                  }}
                >
                  {url}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Content (if present) */}
        {content && (
          <>
            <div style={headingStyle}>Content</div>
            <div
              style={{
                border: `1px solid ${borderColor}`,
                borderRadius: '4px',
                padding: spacing.sm,
                fontSize: fontSize.sm,
                color: textColor,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
                maxHeight: contentExpanded ? undefined : '320px',
                overflow: contentExpanded ? 'visible' : 'auto',
                marginBottom: spacing.sm,
              }}
            >
              {content}
            </div>
            {content.length > 500 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setContentExpanded(!contentExpanded)}
                style={{ padding: 0, marginBottom: spacing.md }}
              >
                {contentExpanded ? 'Collapse' : 'Expand'}
              </Button>
            )}
          </>
        )}

        {/* All fields */}
        <div style={headingStyle}>All Fields</div>
        <JsonTree value={row} defaultExpandDepth={1} />
      </div>
    </div>
  );
}
